import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import "../../styles/RequestsManagement.css";

const RequestsManagement = () => {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("desc"); // Default to newest first

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) throw new Error("No token found");
        
        const res = await axiosInstance.get("/requests", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRequests(res.data);
      } catch (err) {
        console.error(err);
        if (err.response && err.response.status === 401) {
          window.location.href = "/login";
        }
      }
    };

    fetchRequests();
  }, []);

  const getFilteredRequests = () => {
    let filtered = requests;
    if (filter !== "All") {
      filtered = requests.filter((req) => req.status === filter);
    }
    
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axiosInstance.put(
        `/requests/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRequests((prev) =>
        prev.map((req) => (req._id === id ? { ...req, status: newStatus } : req))
      );
      
      if (selectedRequest && selectedRequest._id === id) {
        setSelectedRequest({ ...selectedRequest, status: newStatus });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this request?")) return;
    
    try {
      const token = localStorage.getItem("accessToken");
      await axiosInstance.delete(`/requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setRequests((prev) => prev.filter((req) => req._id !== id));
      setShowModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleSort = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredRequests = getFilteredRequests();

  return (
    <div className="requests-management">
      <div className="requests-header">
        <h3>Manage Requests</h3>
        <div className="requests-controls">
          <div className="filter-tabs">
            {["All", "Pending", "Accepted", "Completed", "Declined"].map((status) => (
              <button
                key={status}
                className={`filter-tab ${filter === status ? "active" : ""}`}
                onClick={() => setFilter(status)}
              >
                {status}
              </button>
            ))}
          </div>
          <button className="sort-btn" onClick={toggleSort}>
            Sort: {sortOrder === "asc" ? "Oldest" : "Newest"}
          </button>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="requests-table-container">
        <table className="requests-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Service</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((request) => (
              <tr key={request._id} onClick={() => { setSelectedRequest(request); setShowModal(true); }}>
                <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                <td>{request.name}</td>
                <td>{request.serviceType}</td>
                <td><span className={`status-badge ${request.status.toLowerCase()}`}>{request.status}</span></td>
                <td>
                  <button className="view-btn">View Details</button>
                </td>
              </tr>
            ))}
            {filteredRequests.length === 0 && (
              <tr>
                <td colSpan="5" className="no-data">No requests found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="requests-grid">
        {filteredRequests.map((request) => (
          <div key={request._id} className="request-card" onClick={() => { setSelectedRequest(request); setShowModal(true); }}>
            <div className="request-card-header">
              <span className="request-date">{new Date(request.createdAt).toLocaleDateString()}</span>
              <span className={`status-badge ${request.status.toLowerCase()}`}>{request.status}</span>
            </div>
            <h4>{request.name}</h4>
            <p className="service-type">{request.serviceType}</p>
            <p className="request-preview">{request.description.substring(0, 60)}...</p>
          </div>
        ))}
        {filteredRequests.length === 0 && (
          <div className="no-data">No requests found</div>
        )}
      </div>

      {/* Details Modal */}
      {showModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>Request Details</h4>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-group">
                <label>Status</label>
                <div className="status-actions">
                  {["Pending", "Accepted", "Completed", "Declined"].map((status) => (
                    <button
                      key={status}
                      className={`status-btn ${status.toLowerCase()} ${selectedRequest.status === status ? "active" : ""}`}
                      onClick={() => handleStatusUpdate(selectedRequest._id, status)}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Customer Name</label>
                  <p>{selectedRequest.name}</p>
                </div>
                <div className="detail-item">
                  <label>Service Type</label>
                  <p>{selectedRequest.serviceType}</p>
                </div>
                <div className="detail-item">
                  <label>Phone</label>
                  <p><a href={`tel:${selectedRequest.phone}`}>{selectedRequest.phone}</a></p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p><a href={`mailto:${selectedRequest.email}`}>{selectedRequest.email}</a></p>
                </div>
                <div className="detail-item full-width">
                  <label>Address</label>
                  <p>{selectedRequest.address}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Description</label>
                  <p>{selectedRequest.description}</p>
                </div>
                <div className="detail-item">
                  <label>Preferred Contact</label>
                  <p>{selectedRequest.preferredContact}</p>
                </div>
                <div className="detail-item">
                  <label>Preferred Time</label>
                  <p>{selectedRequest.preferredTime || "N/A"}</p>
                </div>
                <div className="detail-item full-width">
                  <label>Preferred Days</label>
                  <p>{selectedRequest.preferredDays?.join(", ") || "N/A"}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="delete-btn" onClick={() => handleDelete(selectedRequest._id)}>
                Delete Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsManagement;
